import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconActivity,
  IconAlertCircle,
  IconAlertTriangle,
  IconChevronRight,
  IconCircleCheck,
  IconDroplet,
  IconHeadset,
  IconRouter,
} from "@tabler/icons-react";
import moment from "moment";
import { axiosInstance } from "../../utils/axiosInstance";
import PageContainer from "../layouts/PageContainer";
import {
  Card,
  EmptyState,
  ErrorBanner,
  PageHeader,
  Pill,
  RefreshButton,
  SkeletonRows,
  SkeletonStats,
  Stat,
  Tone,
  activatable,
} from "../ui";
import {
  OpsFillWarning,
  OpsHomePayload,
  OpsRecentError,
  OpsSalesAnomaly,
  OpsSiteGateway,
  OpsSiteRow,
} from "./opsHomeTypes";

const POLL_MS = 60_000;

function formatMoney(n: number): string {
  return `£${n.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function formatPct(ratio: number): string {
  return `${Math.round(ratio * 100)}%`;
}

function formatGeneratedAt(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatErrorAgo(iso: string): string {
  const m = moment(iso);
  return m.isValid() ? m.fromNow() : "—";
}

function gatewayLabel(status: OpsSiteGateway["status"]): string {
  if (status === "online") return "Online";
  if (status === "offline") return "Offline";
  return "Unknown";
}

function gatewayTone(status: OpsSiteGateway["status"]): Tone {
  if (status === "online") return "good";
  if (status === "offline") return "bad";
  return "neutral";
}

function siteGateway(site: OpsSiteRow): OpsSiteGateway {
  return (
    site.gateway || {
      online: 0,
      offline: 0,
      total: 0,
      status: "unknown" as const,
    }
  );
}

function formatErrorAbsolute(iso: string): string {
  const m = moment(iso);
  return m.isValid() ? m.format("DD/MM/YYYY HH:mm:ss") : iso;
}

function formatErrorLabel(row: OpsRecentError): string {
  const code =
    row.errorCode != null && String(row.errorCode) !== ""
      ? String(row.errorCode)
      : "";
  const name =
    row.errorName != null && row.errorName.trim() !== ""
      ? row.errorName.trim()
      : "";
  if (code && name && name !== code) return `${code} — ${name}`;
  if (name) return name;
  if (code) return code;
  return "Unknown error";
}

export default function OpsHome() {
  const navigate = useNavigate();
  const [data, setData] = useState<OpsHomePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const visibleRef = useRef(true);

  const load = useCallback(async (opts?: { soft?: boolean }) => {
    if (opts?.soft) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get<OpsHomePayload>("/ops/home");
      setData(res.data);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load ops home.";
      setError(msg);
      if (!opts?.soft) setData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onVisibility = () => {
      visibleRef.current = document.visibilityState === "visible";
    };
    document.addEventListener("visibilitychange", onVisibility);
    const id = window.setInterval(() => {
      if (visibleRef.current) load({ soft: true });
    }, POLL_MS);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearInterval(id);
    };
  }, [load]);

  const openSiteStatus = (site: OpsSiteRow) => {
    if (!site.layoutSiteId) return;
    navigate(`/status?siteId=${encodeURIComponent(site.layoutSiteId)}`);
  };

  const openErrorStatus = (row: OpsRecentError) => {
    if (!row.layoutSiteId) return;
    navigate(`/status?siteId=${encodeURIComponent(row.layoutSiteId)}`);
  };

  const openSalesAnomaly = (row: OpsSalesAnomaly) => {
    const today = new Date();
    const from = today.toISOString().split("T")[0];
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const to = tomorrow.toISOString().split("T")[0];
    const params = new URLSearchParams({
      siteId: row.locationPin,
      from,
      to,
    });
    navigate(`/customer-service/sales?${params.toString()}`);
  };

  const openFillWarning = (row: OpsFillWarning) => {
    if (!row.layoutSiteId) return;
    navigate(`/status?siteId=${encodeURIComponent(row.layoutSiteId)}`);
  };

  const fillWarningLabel = (row: OpsFillWarning): string => {
    if (row.reason === "busy_and_no_fill") {
      return `${row.inUseWashers}/${row.washerCount} washers in use · ${row.noFillCount1h} no-fill (1h)`;
    }
    if (row.reason === "no_fill_recent") {
      const names = row.noFillMachines
        .map((m) => m.machineName)
        .filter(Boolean)
        .slice(0, 4)
        .join(", ");
      return `${row.noFillCount1h} no-fill in last hour${
        names ? ` · ${names}` : ""
      }`;
    }
    return `${row.inUseWashers}/${row.washerCount} washers in use (threshold ${row.threshold})`;
  };

  const fillWarnings = data?.fillWarnings ?? [];
  const recentErrors = data?.recentErrors ?? [];
  const gatewaysOffline =
    data?.sites.reduce((n, s) => n + (siteGateway(s).offline || 0), 0) ?? 0;

  return (
    <PageContainer>
      <PageHeader
        title="Ops home"
        subtitle={
          data?.generatedAt
            ? `Updated ${formatGeneratedAt(data.generatedAt)}${refreshing ? " · refreshing…" : ""}`
            : undefined
        }
        actions={
          <RefreshButton onClick={() => load({ soft: true })} loading={loading || refreshing} />
        }
      />

      {error ? <ErrorBanner onRetry={() => load()}>{error}</ErrorBanner> : null}

      {loading && !data ? (
        <div className="sq-stack" role="status" aria-label="Loading fleet summary">
          <SkeletonStats count={6} />
          <Card>
            <SkeletonRows rows={6} />
          </Card>
        </div>
      ) : null}

      {data ? (
        <div className="sq-stack">
          <section className="sq-stats" aria-label="Open queues">
            <Stat
              to="/maintenance/open-issues"
              icon={<IconAlertCircle size={16} />}
              label="Open CMMS"
              value={data.cmms.available ? data.cmms.openCount : "—"}
              note={!data.cmms.available ? data.cmms.message : undefined}
            />
            <Stat
              to="/customer-service/helpdesk"
              icon={<IconHeadset size={16} />}
              label="Open helpdesk"
              value={data.helpdesk.openCount}
              note={data.helpdesk.message}
            />
            <Stat
              to="/monitoring/open-incidents"
              icon={<IconActivity size={16} />}
              label="Open incidents"
              tone={
                data.oneuptime.available && data.oneuptime.openIncidentCount > 0
                  ? "warn"
                  : undefined
              }
              value={data.oneuptime.available ? data.oneuptime.openIncidentCount : "—"}
              note={
                !data.oneuptime.available ? data.oneuptime.message : data.oneuptime.projectName
              }
            />
            <Stat
              to="/monitoring/degraded-monitors"
              icon={<IconActivity size={16} />}
              label="Degraded monitors"
              tone={
                data.oneuptime.available && data.oneuptime.degradedMonitorCount > 0
                  ? "warn"
                  : undefined
              }
              value={data.oneuptime.available ? data.oneuptime.degradedMonitorCount : "—"}
              note={!data.oneuptime.available ? data.oneuptime.message : undefined}
            />
            <Stat
              to="/proxy-health"
              icon={<IconRouter size={16} />}
              label="Gateways offline"
              tone={gatewaysOffline > 0 ? "bad" : undefined}
              value={gatewaysOffline}
              note="Alliance site network"
            />
            <Stat
              icon={<IconDroplet size={16} />}
              label="Fill warnings"
              tone={fillWarnings.length > 0 ? "warn" : undefined}
              value={fillWarnings.length}
              note="Busy shop / no-fill"
            />
          </section>

          <div className="sq-columns is-aside-first">
            <aside className="sq-stack">
              <Card
                flush
                title="Fill warnings"
                subtitle="Busy washer load and recent no-fill errors."
                aria-label="Fill warnings"
              >
                {fillWarnings.length === 0 ? (
                  <EmptyState compact icon={<IconCircleCheck size={22} />} title="All clear">
                    No busy-shop or no-fill flags right now.
                  </EmptyState>
                ) : (
                  <ul className="sq-list">
                    {fillWarnings.map((row) => (
                      <li key={`fill-${row.locationId}-${row.reason}`}>
                        <button
                          type="button"
                          className="sq-list-item is-clickable"
                          onClick={() => openFillWarning(row)}
                          disabled={!row.layoutSiteId}
                        >
                          <span className="sq-list-main">
                            <span className="sq-list-title d-block">
                              {row.name}
                              {row.locationPin ? (
                                <span className="sq-muted"> · {row.locationPin}</span>
                              ) : null}
                            </span>
                            <span className="sq-list-meta d-block">{fillWarningLabel(row)}</span>
                          </span>
                          <Pill
                            tone={row.level === "warn" ? "bad" : "warn"}
                            icon={<IconAlertTriangle size={13} />}
                          >
                            {row.level === "warn" ? "Warn" : "Watch"}
                          </Pill>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>

              <Card
                flush
                title="Sales anomalies"
                subtitle="Near-zero card sales vs the same weekday last week (baseline ≥ £50)."
                aria-label="Sales anomalies"
              >
                {data.salesAnomalies.length === 0 ? (
                  <EmptyState compact icon={<IconCircleCheck size={22} />} title="All clear">
                    No near-zero sales flags right now.
                  </EmptyState>
                ) : (
                  <ul className="sq-list">
                    {data.salesAnomalies.map((row) => (
                      <li key={row.locationId}>
                        <button
                          type="button"
                          className="sq-list-item is-clickable"
                          onClick={() => openSalesAnomaly(row)}
                        >
                          <span className="sq-list-main">
                            <span className="sq-list-title d-block">
                              {row.name} <span className="sq-muted">· {row.locationPin}</span>
                            </span>
                            <span className="sq-list-meta d-block">
                              Today {formatMoney(row.todayTotal)} · Last week{" "}
                              {formatMoney(row.baselineTotal)}
                            </span>
                          </span>
                          <Pill tone="warn">{formatPct(row.ratio)} of baseline</Pill>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </aside>

            <div className="sq-stack">
              <Card
                flush
                title="Sites"
                subtitle="Live machine counts. Select a site to open Status."
                aria-label="Sites"
              >
                {data.sites.length === 0 ? (
                  <EmptyState title="No locations configured" />
                ) : (
                  <div className="sq-table-wrap">
                    <table className="sq-table is-stacked cols-5">
                      <thead>
                        <tr>
                          <th scope="col">Site</th>
                          <th scope="col">Gateway</th>
                          <th scope="col" className="num">Available</th>
                          <th scope="col" className="num">In use</th>
                          <th scope="col" className="num">OOO</th>
                          <th scope="col" className="num">Error</th>
                          <th scope="col" className="num">CMMS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.sites.map((site) => {
                          const gw = siteGateway(site);
                          return (
                            <tr
                              key={site.locationId}
                              className={site.layoutSiteId ? "is-clickable" : undefined}
                              {...activatable(() => openSiteStatus(site), !!site.layoutSiteId)}
                              title={
                                site.layoutSiteId
                                  ? `Open status for ${site.name}`
                                  : "No layout site id — status unavailable"
                              }
                            >
                              <td className="cell-title">
                                <div className="sq-strong">{site.name}</div>
                                {site.locationPin ? (
                                  <div className="sq-muted">{site.locationPin}</div>
                                ) : null}
                              </td>
                              <td className="cell-wide nowrap">
                                <Pill tone={gatewayTone(gw.status)}>{gatewayLabel(gw.status)}</Pill>
                                {gw.total > 0 ? (
                                  <span className="sq-muted ms-2">
                                    {gw.online}/{gw.total} online
                                  </span>
                                ) : null}
                              </td>
                              <td className="num" data-label="Avail">
                                {site.counts.available}
                              </td>
                              <td className="num" data-label="In use">
                                {site.counts.inUse}
                              </td>
                              <td
                                className={`num${site.counts.ooo > 0 ? " sq-value-warn" : ""}`}
                                data-label="OOO"
                              >
                                {site.counts.ooo}
                              </td>
                              <td
                                className={`num${site.counts.error > 0 ? " sq-value-bad" : ""}`}
                                data-label="Error"
                              >
                                {site.counts.error}
                              </td>
                              <td className="num" data-label="CMMS">
                                {site.cmmsOpenCount > 0 ? (
                                  <Pill tone="warn">{site.cmmsOpenCount}</Pill>
                                ) : (
                                  "—"
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>

              <Card
                flush
                title="Recent errors"
                subtitle="Last 10 stored machine errors."
                aria-label="Recent errors"
              >
                {recentErrors.length === 0 ? (
                  <EmptyState compact icon={<IconCircleCheck size={22} />} title="All clear">
                    No recent errors recorded.
                  </EmptyState>
                ) : (
                  <div className="sq-table-wrap">
                    <table className="sq-table is-stacked">
                      <thead>
                        <tr>
                          <th scope="col">When</th>
                          <th scope="col">Site</th>
                          <th scope="col">Machine</th>
                          <th scope="col">Error</th>
                          <th scope="col" aria-label="Open" />
                        </tr>
                      </thead>
                      <tbody>
                        {recentErrors.map((row) => (
                          <tr
                            key={row.id}
                            className={row.layoutSiteId ? "is-clickable" : undefined}
                            {...activatable(() => openErrorStatus(row), !!row.layoutSiteId)}
                            title={
                              row.layoutSiteId
                                ? `Open status for ${row.locationName || "site"}`
                                : "No layout site id — status unavailable"
                            }
                          >
                            <td data-label="When" className="nowrap">
                              <div className="sq-strong">{formatErrorAgo(row.occurredAt)}</div>
                              <div className="sq-muted">{formatErrorAbsolute(row.occurredAt)}</div>
                            </td>
                            <td className="cell-title">
                              <div className="sq-strong">{row.locationName || "Unknown site"}</div>
                              {row.locationPin ? (
                                <div className="sq-muted">{row.locationPin}</div>
                              ) : null}
                            </td>
                            <td data-label="Machine" className="sq-strong">
                              {row.machineName}
                            </td>
                            <td data-label="Error" className="cell-wide">
                              {formatErrorLabel(row)}
                            </td>
                            <td className="cell-actions d-none d-md-table-cell">
                              <IconChevronRight size={16} className="text-secondary" />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </div>
          </div>
        </div>
      ) : null}
    </PageContainer>
  );
}
